/**
 * Inbox feature — Zod schemas and derived types.
 *
 * The wire mirror of `backend/internal/ops/report/inbox_contract.go`. That file
 * is the authority; this is the client half of the same contract, and the two
 * are meant to be read side by side.
 *
 * # The one inbox
 *
 * Tenant Home shows ONE inbox with ONE badge (§B.1). Ten decision queues feed
 * it. The invariant is not "everything has a deadline" — it is that every row
 * names its next automatic state change and when.
 */

import { z } from "zod";

/** Source queue. Wire-stable; mirrors InboxKind in Go. */
export const inboxKindSchema = z.enum([
  "agent_action_approval",
  "agent_delegation_request",
  "access_request",
  "credential_recovery_request",
  "admin_recovery_request",
  "recovery_request",
  "frontline_activation_request",
  "budget_supplement",
  "agent_subagent_change_request",
  // An administrator who cannot reach the console at all, asking to be let in
  // (account_access_requests — People → Sign-in requests). Added last: it was
  // the one decision queue absent from the one inbox, so Home showed a clean
  // badge while an administrator was locked out.
  "sign_in_request",
]);
export type InboxKind = z.infer<typeof inboxKindSchema>;

/**
 * What happens if nobody acts. Five values, closed.
 *
 * `widened` is the only NON-terminal verb, which is why it is the only one that
 * carries a nested `then`: an escalation moves the row to a wider approver pool
 * and the row then goes on to cancel or lapse. That single nesting is what lets
 * one grammar hold every queue.
 *
 * `stays_locked_out` is the verb that asserts NOTHING will happen automatically,
 * and it exists because "no automatic transition" and "no consequence" are
 * different things. Conflating them is what made the mock's original
 * "Doing nothing is safe" false for its own recovery row.
 */
export const consequenceVerbSchema = z.enum([
  "refused",
  "widened",
  "cancelled",
  "stays_locked_out",
  "lapses",
]);
export type ConsequenceVerb = z.infer<typeof consequenceVerbSchema>;

/**
 * Recursive because `widened` nests. Zod needs the explicit type annotation and
 * `z.lazy` for a self-referential schema.
 */
export interface Consequence {
  verb: ConsequenceVerb;
  at: string | null;
  then?: Consequence;
}

export const consequenceSchema: z.ZodType<Consequence> = z.lazy(() =>
  z.object({
    verb: consequenceVerbSchema,
    at: z.string().nullable(),
    then: consequenceSchema.optional(),
  }),
);

export const inboxRowSchema = z.object({
  kind: inboxKindSchema,
  id: z.string(),
  // `href` IS STILL ON THE WIRE AND IS DELIBERATELY NOT PARSED HERE.
  //
  // It carried `/inbox/{kind}/{id}` for eight queues and `/decisions/{id}` for
  // the ninth. Neither prefix has ever existed as a route in either app, so
  // every "Open" button on the tenant overview 404'd, and `/decisions/` was
  // wrong twice over — it also keys on `approval_id` while every portal surface
  // keys on `auth_req_id`.
  //
  // Its docstring justified the split by claiming an approval's `human_sub`
  // must be a `users` row so an admin "cannot be a human_sub at all". That is
  // false: the column carries no foreign key and an admin-delegated grant
  // records the admin's `admin_users.id`. The routing it argued for is still
  // right, but for a stronger reason — the approval ceremony pins its WebAuthn
  // origin to the portal host, and widening that to the admin host would cut
  // phishing resistance on the highest-consequence action in the product.
  //
  // Routing now lives in inbox-row.tsx as literal typed paths, where the
  // compiler checks them. Parsing this field again would re-open the hole.
  ask: z.string(),
  subject: z.string(),
  /** Sort tiebreak only. §D.2 replaced the age column with time remaining:
   *  "age is vanity, the deadline is the decision." Do not render this. */
  requested_at: z.string(),
  /** LEAST(escalation, decision deadline) — the sort key and the one instant a
   *  row displays. */
  next_change_at: z.string().nullable(),
  consequence: consequenceSchema,
});
export type InboxRow = z.infer<typeof inboxRowSchema>;

export const inboxResponseSchema = z.object({
  rows: z.array(inboxRowSchema),
});
export type InboxResponse = z.infer<typeof inboxResponseSchema>;

/**
 * A Home pulse percentage with its denominator and whether it was measured.
 *
 * `measured: false` is NOT zero. It means the underlying signal does not exist,
 * and the tile must render `note` instead of a figure. Serializing an
 * unmeasurable metric as 0 and drawing "0% of sign-ins were phishing-resistant"
 * is not an empty state, it is a false measurement — the same class of claim as
 * the `0 incidents` tile §B.1 deleted for being unmeasurable.
 *
 * The numeric fields are nullable on the wire precisely so a client cannot
 * accidentally render a measurement that was never taken.
 */
export const pulseMetricSchema = z.object({
  measured: z.boolean(),
  percent: z.number().nullable(),
  numerator: z.number().nullable(),
  denominator: z.number().nullable(),
  note: z.string(),
});
export type PulseMetric = z.infer<typeof pulseMetricSchema>;
